/**
 * ManageGallery.jsx
 * Admin CRUD for Gallery + File Upload
 */

import { useState, useEffect } from "react";
import {
  BSModal,
  DeleteModal,
  Field,
  PageHeader,
  Loading,
  EmptyState,
  apiFetch,
} from "../../components/helpers";

const EMPTY_FORM = {
  gallery_id: null,
  title: "",
  image: null,
  old_image: "",
  category: "Clinic",
  status: "Active",
};

export default function ManageGallery() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);

    try {
      const json = await apiFetch(
        `${process.env.REACT_APP_GALLERY_ADMIN_VIEW}`,
      );

      if (json.status) {
        setRows(json.data || []);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      ...row,
      image: null,
      old_image: row.image,
    });

    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError("Title required");
      return;
    }
    console.log(
      process.env.REACT_APP_API_URL + process.env.REACT_APP_GALLERY_INSERT,
    );
    setSubmitting(true);
    setError("");

    try {
      const isEdit = !!form.gallery_id;

      const fd = new FormData();

      fd.append("gallery_id", form.gallery_id || "");
      fd.append("title", form.title);
      fd.append("category", form.category);
      fd.append("status", form.status);
      fd.append("old_image", form.old_image || "");

      if (form.image) {
        fd.append("image", form.image);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}${
          isEdit
            ? process.env.REACT_APP_GALLERY_UPDATE
            : process.env.REACT_APP_GALLERY_INSERT
        }`,
        {
          method: "POST",
          body: fd,
        },
      );

      const json = await res.json();

      if (json.status) {
        setModalOpen(false);
        load();
      } else {
        setError(json.message || "Failed");
      }
    } catch {
      setError("Network Error");
    }

    setSubmitting(false);
  };

  const handleDelete = async () => {
  try {
    const fd = new FormData();
    fd.append("gallery_id", deleteId);

    const res = await fetch(
      `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_GALLERY_DELETE}`,
      {
        method: "POST",
        body: fd,
      }
    );

    const json = await res.json();

    if (json.status) {
      setDeleteId(null);
      load();
    } else {
      alert(json.message);
    }
  } catch (err) {
    console.error(err);
  }
};

  const imgUrl = (img) => {
  if (!img) return "";

  const clean = img.replace(/\\/g, "/").trim();

  const baseUrl =process.env.REACT_APP_WEB_URL;

  return `${baseUrl}/${clean.replace(/^\/+/, "")}`;
};

  return (
    <div className="p-3 p-lg-4">
      <PageHeader title="Manage Gallery" subtitle={`${rows.length} image(s)`}>
        <button
          className="btn text-white fw-bold"
          style={{
            background: "#1D9E75",
            borderRadius: 10,
          }}
          onClick={openAdd}
        >
          + Add Image
        </button>
      </PageHeader>

      <div
        className="card border-0 shadow-sm"
        style={{
          borderRadius: 16,
        }}
      >
        <div className="card-body p-0">
          {loading ? (
            <Loading />
          ) : rows.length === 0 ? (
            <EmptyState message="No gallery found" />
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead
                  style={{
                    background: "#FAFBFC",
                  }}
                >
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Image</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r) => (
                    <tr key={r.gallery_id}>
                      <td className="px-4">{r.title}</td>

                      <td className="px-4">{r.category}</td>

                      <td className="px-4">
                        {r.image ? (
                          <img
                            src={imgUrl(r.image)}
                            alt=""
                            style={{
                              width: 60,
                              height: 40,
                            }}
                          />
                        ) : (
                          <span>No Image</span>
                        )}
                      </td>

                      <td className="px-4">
                        <span
                          className={`badge ${
                            r.status === "Active"
                              ? "text-bg-success"
                              : "text-bg-secondary"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>

                      <td className="px-4">
                        <div className="d-flex gap-2">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => openEdit(r)}
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => setDeleteId(r.gallery_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <BSModal
        open={modalOpen}
        title={form.gallery_id ? "Edit Image" : "Add Image"}
        onClose={() => setModalOpen(false)}
      >
        <div className="row g-3">
          <div className="col-md-6">
            <Field label="Title">
              <input
                className="form-control"
                value={form.title}
                onChange={(e) =>
                  setForm({
                    ...form,
                    title: e.target.value,
                  })
                }
              />
            </Field>
          </div>

          <div className="col-md-6">
            <Field label="Upload Image">
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={(e) =>
                  setForm({
                    ...form,
                    image: e.target.files[0],
                  })
                }
              />
            </Field>
          </div>

          <div className="col-md-6">
            <Field label="Category">
              <select
                className="form-select"
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value,
                  })
                }
              >
                <option>Clinic</option>
                <option>Treatments</option>
                <option>Team</option>
                <option>Before & After</option>
                <option>Events</option>
              </select>
            </Field>
          </div>

          <div className="col-md-6">
            <Field label="Status">
              <select
                className="form-select"
                value={form.status}
                onChange={(e) =>
                  setForm({
                    ...form,
                    status: e.target.value,
                  })
                }
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </Field>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button className="btn btn-light" onClick={() => setModalOpen(false)}>
            Cancel
          </button>

          <button
            className="btn text-white"
            style={{
              background: "#1D9E75",
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Save"}
          </button>
        </div>
      </BSModal>

      <DeleteModal
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
